import { NavigationInfo } from '../../../models/navigation-info';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { Endpoint, DataAreaType } from '../../../models/enums';
import { Template, TemplateType } from '../../../models/template';
import { Role } from '../../../models/role';
import { ContextMenuOption } from '../../../models/context-menu-option';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { ObjectActionListType, PermissionSet, ActionType, Permissions } from '../../../models/permission';

const templatesContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'Save to Template ...',
        iconClass: 'fa-save', iconColor: 'color-blue-01',
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Load from Template ...', iconClass: 'fa-folder-open', iconColor: 'color-yellow-07',
        visible: (item: GenericTreeNode) => (item.data as Template).templateType !== TemplateType.CUSTOM_FIELDS,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        divider: true,
        visible: (item: GenericTreeNode) => (item.data as Template).templateType === TemplateType.EXPORT_FIELDS || (item.data as Template).templateType === TemplateType.DOCUMENT_METADATA,
    }),
    new ContextMenuOption({
        name: 'Download as XML ...', iconClass: 'fa-download', iconColor: 'color-blue-01',
        visible: (item: GenericTreeNode) => (item.data as Template).templateType === TemplateType.EXPORT_FIELDS || (item.data as Template).templateType === TemplateType.DOCUMENT_METADATA,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Load from XML ...', iconClass: 'fa-folder-open', iconColor: 'color-yellow-07',
        visible: (item: GenericTreeNode) => (item.data as Template).templateType === TemplateType.EXPORT_FIELDS || (item.data as Template).templateType === TemplateType.DOCUMENT_METADATA,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })],
        tooltip: 'A new template will be created from the XML file'
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Set as Default', iconClass: 'fa-check', iconColor: 'color-green-04',
        enabled: (item: GenericTreeNode) => !(item.data as Template).defaultTemplate,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Edit ...',
        iconClass: 'fa-pencil', iconColor: 'color-blue-01',
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05',
        enabled: (item: GenericTreeNode) => !(item.data as Template).defaultTemplate,
        permissions: [new PermissionSet({ permissionActions: [ActionType.DELETE] })]
    })
];

const templateHeaderButtons: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'New Template ...',
        iconClass: 'fa-plus', iconColor: 'color-green-04',
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
];

const headerContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'Download All as XMLs ...', iconClass: 'fa-download', iconColor: 'color-blue-01',
        visible: (item: GenericTreeNode) => item.requestInfo.data.tempType === TemplateType.EXPORT_FIELDS || item.requestInfo.data.tempType === TemplateType.DOCUMENT_METADATA,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Load from XMLs ...', iconClass: 'fa-folder-open', iconColor: 'color-yellow-07',
        visible: (item: GenericTreeNode) => item.requestInfo.data.tempType === TemplateType.EXPORT_FIELDS || item.requestInfo.data.tempType === TemplateType.DOCUMENT_METADATA,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })],
        tooltip: 'A new template will be created from each XML file'
    })
];

const rolePermissionContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'Set as Default', iconClass: 'fa-check', iconColor: 'color-green-04',
        enabled: (item: GenericTreeNode) => ((item.data as Role).attributes[0].value === 'false'),
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ROLE, permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01',
        enabled: (item: GenericTreeNode) => !((item.data as Role).name === 'Organization Administrator'),
        visible: (item: GenericTreeNode) => !((item.data as Role).name === 'Organization Administrator'),
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ROLE, permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Copy ...', iconClass: 'fa-copy', iconColor: 'color-grey-10',
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ROLE, permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05',
        enabled: (item: GenericTreeNode) => !((item.data as Role).name === 'Organization Administrator') && ((item.data as Role).attributes[0].value === 'false'),
        visible: (item: GenericTreeNode) => !((item.data as Role).name === 'Organization Administrator'),
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ROLE, permissionActions: [ActionType.DELETE] })]
    })
];

const rolePermissionHeaderButtons: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'New Role ...', iconClass: 'fa-plus', iconColor: 'color-green-04',
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    })
];

export const OrganizationMenu: Array<GenericTreeNode> = [
    new GenericTreeNode({
        name: 'General',
        automationId: 'org-settings-tree-general',
        isExpandable: true,
        isExpanded: true,
        isExpandedOnSelect: true,
        isSelectable: false,
        hideExpander: true,

        children: [
            new GenericTreeNode({
                name: 'Connectors',
                automationId: 'org-settings-tree-general-connectors',
                icon: 'fa-plug',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: 'Connectors', path: 'settings-connectors-list' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.CONNECTOR })]
            }),
            new GenericTreeNode({
                name: 'Storage',
                automationId: 'org-settings-tree-general-storage',
                icon: 'fa-dropbox',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: 'Storage', path: 'settings-storage' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.CONNECTOR })]
            }),
            new GenericTreeNode({
                name: 'Project Data Areas',
                automationId: 'org-settings-tree-general-project-data-areas',
                icon: 'fa-database',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                bundle: DataAreaType.PROJECT,
                navigationInfo: new NavigationInfo({ name: 'Project Data Areas', path: 'settings-data-areas' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PROJECT_DATA_AREA })]
            }),
            new GenericTreeNode({
                name: 'Export Data Areas',
                automationId: 'org-settings-tree-general-export-data-areas',
                icon: 'fa-database',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                bundle: DataAreaType.EXPORT,
                navigationInfo: new NavigationInfo({ name: 'Export Data Areas', path: 'settings-export-data-areas' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.EXPORT_DATA_AREA })]
            }),
            new GenericTreeNode({
                name: 'Shared Data Sets',
                automationId: 'org-settings-tree-general-shared-data-sets',
                icon: 'fa-cube',
                iconSecond: 'fa-circle-thin',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                bundle: { isSharedDataSetNode: true },
                navigationInfo: new NavigationInfo({ name: 'Shared Data Sets', path: 'settings-shared-data-sets' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SHARED_DATA_SET })]
            }),
            new GenericTreeNode({
                name: 'Export Databases',
                automationId: 'org-settings-tree-general-export-databases',
                icon: 'fa-cloud-upload',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: 'Export Databases', path: 'settings-export-databases' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.EXPORT_DB_CONNECTION })]
            }),
            new GenericTreeNode({
                name: 'Role Permissions',
                automationId: 'org-settings-tree-general-roles',
                icon: 'fa-user-md',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: rolePermissionHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listRoles,
                    responseName: 'roles',
                    data: { objectType: 'ALL', returnObjectActionLists: false }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ROLE })],
                createChild: (data: any): GenericTreeNode => {
                    const role = new Role(data);
                    let isDefaultRole = 'false';
                    if (role && role.attributes) {
                        const itemIndex = role.attributes.findIndex(item => item.name === 'defaultRole');
                        if (itemIndex >= 0) {
                            isDefaultRole = role.attributes[itemIndex].value;
                        }
                    }
                    return new GenericTreeNode({
                        name: role.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-role-permissions' }),
                        customText: isDefaultRole === 'true' ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: true,
                        contextMenuOptions: Permissions.checkUserPermission(ObjectActionListType.ROLE, ActionType.CREATE) ? rolePermissionContextMenuOptions : null,
                        data: role
                    });
                }
            }),
            new GenericTreeNode({
                name: 'Organization Users',
                automationId: 'org-settings-tree-general-organization-users',
                icon: 'fa-user',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-users-permission' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.USERS })]
            }),
            new GenericTreeNode({
                name: 'Organization Groups',
                automationId: 'org-settings-tree-general-organization-groups',
                icon: 'fa-users',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-groups' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.GROUPS })]
            }),
            new GenericTreeNode({
                name: 'Container Key Files',
                automationId: 'org-settings-tree-general-container-key-files',
                icon: 'fa-key',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-project-container-key-files' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.CONTAINER_KEY_FILES })]
            })
        ]
    }),
    new GenericTreeNode({
        name: 'Reef Express',
        automationId: 'org-settings-tree-tlsexpress',
        isExpandable: true,
        isExpanded: true,
        isExpandedOnSelect: true,
        isSelectable: false,
        hideExpander: true,
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.TLS_PM_EMAIL_RECIPIENTS }), new PermissionSet({ permissionType: ObjectActionListType.TLS_SALES_EMAIL_RECIPIENTS })],

        children: [
            new GenericTreeNode({
                name: 'Preferences',
                automationId: 'org-settings-tree-tlsexpress-preferences',
                icon: 'fa-gear',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-preferences' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.REEF_EXPRESS_PREFERENCES })]
            }),
            new GenericTreeNode({
                name: 'PM Email Lists',
                automationId: 'org-settings-tree-tlsexpress-pm-email-lists',
                icon: 'fa-envelope',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-tls-pm-email-lists' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.TLS_PM_EMAIL_RECIPIENTS })]
            }),
            new GenericTreeNode({
                name: 'Sales Email Lists',
                automationId: 'org-settings-tree-tlsexpress-sales-email-lists',
                icon: 'fa-envelope',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-tls-sales-email-lists' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.TLS_SALES_EMAIL_RECIPIENTS })]
            }),
        ]
    }),
    new GenericTreeNode({
        name: 'eDiscovery Templates',
        automationId: 'org-settings-tree-templates',
        isExpandable: true,
        isExpanded: true,
        isExpandedOnSelect: true,
        isSelectable: false,
        hideExpander: true,

        children: [
            new GenericTreeNode({
                name: 'Alias Lists',
                automationId: 'org-settings-tree-templates-alias-lists',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.ALIAS_LISTS }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_ALIAS_LISTS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ path: 'settings-alias-lists' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_ALIAS_LISTS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_ALIAS_LISTS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Analytic Settings',
                automationId: 'org-settings-tree-templates-analytic-settings',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.ANALYTICAL_SETTINGS }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_ANALYTIC_SETTINGS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ path: 'settings-analytic-settings' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_ANALYTIC_SETTINGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_ANALYTIC_SETTINGS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Billing Reports',
                automationId: 'org-settings-tree-templates-billing-reports',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.BILLING_REPORT_SETTINGS }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_BILLING_REPORTS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-billing-report' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_BILLING_REPORTS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_BILLING_REPORTS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Custom Fields',
                automationId: 'org-settings-tree-templates-custom-fields',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.CUSTOM_FIELDS }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_CUSTOM_FIELDS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-custom-fields' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_CUSTOM_FIELDS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_CUSTOM_FIELDS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Domain Lists',
                automationId: 'org-settings-tree-templates-domain-lists',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.DOMAIN_LISTS }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_DOMAIN_LISTS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-domain-lists' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_DOMAIN_LISTS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_DOMAIN_LISTS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Excluded Content',
                automationId: 'org-settings-tree-templates-excluded-content',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.EMAIL_SIGNATURE }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_EXCLUDED_CONTENT })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: 'Shared Data Sets', path: 'settings-excluded-content' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_EXCLUDED_CONTENT, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_EXCLUDED_CONTENT })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Export Fields',
                automationId: 'org-settings-tree-templates-export-fields',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.EXPORT_FIELDS }
                }),
                contextMenuOptions: headerContextMenuOptions,
                showContextMenuButton: true,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_EXPORT_FIELDS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-export-fields' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_EXPORT_FIELDS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_EXPORT_FIELDS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Export Settings',
                automationId: 'org-settings-tree-templates-export-settings',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.EXPORT_SETTINGS }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_EXPORT_SETTINGS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-export-settings' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_EXPORT_SETTINGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_EXPORT_SETTINGS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Index Settings',
                automationId: 'org-settings-tree-templates-index-settings',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.INDEX_SETTINGS }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_INDEX_SETTINGS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-index-settings' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_INDEX_SETTINGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_INDEX_SETTINGS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Load File Import',
                automationId: 'org-settings-tree-templates-load-file-import',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.LOADFILE_SETTINGS }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_LOADFILE_SETTINGS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-load-file-import' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_LOADFILE_SETTINGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_LOADFILE_SETTINGS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Metadata Search Fields',
                automationId: 'org-settings-tree-templates-metadata-search-fields',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.SEARCH_FIELDS }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_METADATA_SEARCH_FIELDS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-metadata-fields' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_METADATA_SEARCH_FIELDS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_METADATA_SEARCH_FIELDS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Metadata View Fields',
                automationId: 'org-settings-tree-templates-metadata-view-fields',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.DOCUMENT_METADATA }
                }),
                contextMenuOptions: headerContextMenuOptions,
                showContextMenuButton: true,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_METADATA_VIEW_FIELDS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-metadata-fields' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_METADATA_VIEW_FIELDS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_METADATA_VIEW_FIELDS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Patterns',
                automationId: 'org-settings-tree-templates-patterns',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.USER_EXP }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_REGULAR_EXPRESSIONS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-patterns' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_REGULAR_EXPRESSIONS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_REGULAR_EXPRESSIONS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Reports',
                automationId: 'org-settings-tree-templates-reports',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.REPORT_SETTINGS }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_REPORT_SETTINGS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-report-settings' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_REPORT_SETTINGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_REPORT_SETTINGS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Search Settings',
                automationId: 'org-settings-tree-templates-search-settings',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.SEARCH_SETTINGS }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_SEARCH_SETTINGS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-search-settings' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_SEARCH_SETTINGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_SEARCH_SETTINGS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Survivorship Model',
                automationId: 'org-settings-tree-templates-survivorship-model',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.DUPE_SURVIVORSHIP }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_SURVIVORSHIP_SETTINGS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-survivorship' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_SURVIVORSHIP_SETTINGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_SURVIVORSHIP_SETTINGS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Tags',
                automationId: 'org-settings-tree-templates-tags',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.TAG }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_TAGS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-tags-settings' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_TAGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_TAGS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Workflows',
                automationId: 'org-settings-tree-templates-workflows',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'ORG_LEVEL', tempType: TemplateType.WORKFLOWS }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_WORKFLOWS })], 
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-workflows' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-yellow-07',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.ORG_WORKFLOWS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORG_WORKFLOWS })],
                    });
                },
            })
        ]
    })
];

export const ReviewMenu: Array<GenericTreeNode> = [
    new GenericTreeNode({
        name: 'Review Templates',
        automationId: 'org-settings-tree-review-templates',
        isExpandable: true,
        isExpandedOnSelect: true,
        isExpanded: true,
        isSelectable: false,
        hideExpander: true,
        children: [
            new GenericTreeNode({
                name: 'Review Set',
                automationId: 'org-settings-tree-review-templates-review-set',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-yellow-07',
                childPadding: 5,
            })
        ]
    })
];
